import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useConfirmDialog } from '@bluheadless/ui-logic/src/hooks/confirm-dialog'
import { useCatalogUrl } from '@bluheadless/ui-logic/src/hooks/useCatalogUrl'
import { useNotification } from '@bluheadless/ui-logic/src/hooks/useNotification'
import { useProductContext } from '@bluheadless/ui-logic/src/providers/product'
import { useQuickBuy } from '@bluheadless/ui-logic/src/providers/quick-buy'
import { useProductOptionsForQueryParams } from '@bluheadless/ui-logic/src/hooks/product/useProductOptionsForQueryParams'
import { ValidationError } from '@bluheadless/ui-logic/src/errors'
import { elementType } from 'prop-types'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useProductPriceProps } from '../../product-price'
import TileWishListMiddleware from './tile-wish-list-middleware'
import { useTrackingsActions } from '@bluheadless/ui-logic/src/hooks/trackings'
import { usePageContext } from '@bluheadless/ui-logic/src/providers/page/page'
import { useUserUniqueId } from '@bluheadless/ui-logic/src/hooks/useUserUniqueId'
import { useUser } from '@bluheadless/ui-logic/src/providers/user'
import { useCurrency } from '@bluheadless/ui-logic/src/providers/currency'

const CAN_EDIT_WISH_LIST = ['expanded']

const TileWishListBridge = ({
	priceProps,
	variant,
	onClick,
	pageContext,
	pageId: _pageId,
	widgetContext,
	...props
}) => {
	const [
		{
			product,
			selectedProduct,
			loadingAddToCart,
			loadingMoveToCart,
			loadingWishList,
			wishListItem,
			qty,
			assets,
			inventory: { inStock, saleable },
			readyToAddToCart,
			selectedOptions,
			childrenFormDefaultValues,
			selectedOptionsIdsValues,
			configurableChildrenProducts,
			groupedChildrenProducts,
			bundleProducts,
		},
		{ wishListChangeQty, moveFromWishListToCart, removeFromWishList },
	] = useProductContext()

	const { configurableOptions = [], bundleOptions = [] } = product

	const productOptionsForQueryParams = useProductOptionsForQueryParams({
		selectedOptionsIdsValues,
		configurableOptions,
		bundleOptions,
	})

	const { formatMessage } = useIntl()
	const {
		catalog: {
			products: { images: { height: height, width: width } = {} },
		},
	} = useConfig()

	const { name, urlKey } = product
	const productUrl = useCatalogUrl(urlKey, productOptionsForQueryParams)

	const { enqueueNotification } = useNotification()
	const { isConfirmed } = useConfirmDialog()

	const [, { openQuickBuy }] = useQuickBuy()
	const { productClick } = useTrackingsActions()
	const { entityType: pageType, __bhEntityId: pageId } = usePageContext()
	const userUniqueId = useUserUniqueId()
	const [{ isLoggedIn }] = useUser()
	const productPriceProps = useProductPriceProps({ displayContext: 'tiles', ...priceProps })

	const handleRemoveFromWishList = useCallback(async () => {
		const confirmed = await isConfirmed(formatMessage({ id: 'wishlist_confirm_remove_item' }, { productName: name }))
		if (confirmed) {
			try {
				await removeFromWishList()
				enqueueNotification({ uiProps: { message: formatMessage({ id: 'deleted_success' }), severity: 'success' } })
			} catch (error) {
				enqueueNotification({
					uiProps: {
						severity: 'error',
						message: formatMessage({ id: 'wishlist_cant_remove_item' }, { productName: name }),
					},
				})
			}
		}
	}, [name, isConfirmed, removeFromWishList, enqueueNotification, formatMessage])

	const handleDecrement = useCallback(async () => {
		try {
			await wishListChangeQty({ qty: qty - 1 })
		} catch (error) {
			enqueueNotification({
				uiProps: { message: formatMessage({ id: 'wishlist_cant_update_qty' }), severity: 'error' },
			})
		}
	}, [enqueueNotification, wishListChangeQty, qty, formatMessage])

	const handleIncrement = useCallback(async () => {
		try {
			await wishListChangeQty({ qty: qty + 1 })
		} catch (error) {
			enqueueNotification({
				uiProps: { message: formatMessage({ id: 'wishlist_cant_update_qty' }), severity: 'error' },
			})
		}
	}, [enqueueNotification, wishListChangeQty, qty, formatMessage])

	const handleFromWishListToCart = useCallback(
		async (e) => {
			try {
				await moveFromWishListToCart(() => {
					throw new ValidationError()
				}, e)
				enqueueNotification({
					uiProps: {
						message: formatMessage({ id: 'cart_item_added' }, { productName: name }),
						severity: 'success',
					},
				})
			} catch (error) {
				if (error instanceof ValidationError) {
					// skip validation error
					return
				}
				console.error(error)
				enqueueNotification({
					uiProps: {
						message: formatMessage({ id: 'wishlist_cant_move_to_cart' }, { productName: name }),
						severity: 'error',
					},
				})
			}
		},
		[moveFromWishListToCart, enqueueNotification, formatMessage, name]
	)
	const { currency } = useCurrency()

	return (
		<TileWishListMiddleware
			addToCartProps={{
				loading: loadingAddToCart || loadingMoveToCart || loadingWishList,
				disabled: !inStock,
				onClick: readyToAddToCart
					? handleFromWishListToCart
					: () =>
							openQuickBuy({
								product,
								action: 'moveFromWishListToCart',
								actionLabel: formatMessage({ id: 'action_add_to_cart' }),
								formDefaultValues: selectedOptions,
								defaultQty: qty,
								linkedWishListItemId: wishListItem?.id,
								childrenFormDefaultValues,
								pageContext: pageContext ?? pageType,
								pageId: _pageId ?? pageId,
								widgetContext,
							}),
			}}
			editProps={
				CAN_EDIT_WISH_LIST.includes(variant)
					? {
							disabled: loadingWishList,
							loading: loadingWishList,
							onClick: () =>
								openQuickBuy({
									product,
									action: 'wishListItemUpdate',
									actionLabel: formatMessage({ id: 'action_update_wishlist_item' }),
									formDefaultValues: selectedOptions,
									defaultQty: qty,
									linkedWishListItemId: wishListItem?.id,
									childrenFormDefaultValues,
									pageContext: pageContext ?? pageType,
									pageId: _pageId ?? pageId,
									widgetContext,
								}),
					  }
					: {}
			}
			priceProps={
				productPriceProps.hide
					? null
					: {
							amount: productPriceProps.amount,
							currency: productPriceProps.currency,
							currencyDisplay: productPriceProps.currencyDisplay,
							currencyPosition: productPriceProps.currencyPosition,
							showPercentage: productPriceProps.showPercentage,
							specialAmount: productPriceProps.specialAmount,
							middlePrice: productPriceProps.middlePrice,
							mode: productPriceProps.mode,
							oldPrice: productPriceProps.oldPrice,
					  }
			}
			productProps={{
				assets,
				product: selectedProduct ?? product,
				qty,
				imageHeight: height,
				imageWidth: width,
				inStock,
				saleable,
			}}
			qtyProps={{
				loading: loadingWishList,
				onDecrement: handleDecrement,
				onIncrement: handleIncrement,
				qty,
			}}
			url={productUrl}
			onClick={(e) => {
				productClick({
					params: {
						product,
						position: props.position,
						userUniqueId,
						currency,
						groupedChildrenProducts,
						configurableChildrenProducts,
						bundleProducts,
						isLoggedIn,
					},
					pageContext: pageContext ?? pageType,
					pageId: _pageId ?? pageId,
					widgetContext,
				})
				if (onClick) {
					onClick(e, { product })
				}
			}}
			wishListRemoveProps={{
				loading: loadingWishList,
				disabled: loadingWishList,
				onClick: handleRemoveFromWishList,
			}}
			{...props}
		/>
	)
}

TileWishListBridge.defaultProps = {}

TileWishListBridge.propTypes = {
	/**
	 * The component to render the tile with.
	 */
	Tile: elementType,
}

export default TileWishListBridge
